import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import EmptyState from "@/components/EmptyState";
import Table, {
  TableHeading,
  TableFooter,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { renderEmptyCellState } from "@/components/Table/utils";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { AddStaffMemberModal } from "./_AddStaffMemberModal";
import { EditStaffRoleModal } from "./_EditStaffRoleModal";
import { RemoveStaffMemberDialog } from "./_RemoveStaffMemberDialog";
import { UNIVERSITY_STAFF_QUERY } from "./queries";
import {
  CREATE_UNIVERSITY_STAFF_MUTATION,
  DELETE_UNIVERSITY_STAFF_MUTATION,
  UPDATE_UNIVERSITY_STAFF_MUTATION
} from "./mutations";
import type { UniversityStaffMember, UniversityStaffRole } from "./types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { UNIVERSITY_STAFF_ROLE_LABELS } from "./types";

export function UniversityStaffTable({
  universityLegacyId,
  universityName
}: {
  universityLegacyId: string;
  universityName: string;
}) {
  const { data, loading } = useQuery(UNIVERSITY_STAFF_QUERY, {
    variables: { legacyId: universityLegacyId }
  });

  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState<UniversityStaffMember | null>(null);

  const refetchQuery = {
    query: UNIVERSITY_STAFF_QUERY,
    variables: { legacyId: universityLegacyId }
  };

  const [createUniversityStaff, { loading: creatingStaff }] = useMutation(CREATE_UNIVERSITY_STAFF_MUTATION, {
    onCompleted: (result) => {
      if (result.createUniversityStaff.success) {
        showSnackbar(result.createUniversityStaff.message || "Staff member added");
        setIsAddModalOpen(false);
      } else {
        showErrorSnackbar(result.createUniversityStaff.message || "Failed to add staff member");
      }
    },
    onError: () => showErrorSnackbar("Failed to add staff member"),
    refetchQueries: [refetchQuery],
    awaitRefetchQueries: true
  });

  const [updateUniversityStaff, { loading: updatingStaff }] = useMutation(UPDATE_UNIVERSITY_STAFF_MUTATION, {
    onCompleted: (result) => {
      if (result.updateUniversityStaff.success) {
        showSnackbar(result.updateUniversityStaff.message || "Staff role updated");
        setIsEditModalOpen(false);
        setSelectedStaffMember(null);
      } else {
        showErrorSnackbar(result.updateUniversityStaff.message || "Failed to update staff role");
      }
    },
    onError: () => showErrorSnackbar("Failed to update staff role"),
    refetchQueries: [refetchQuery],
    awaitRefetchQueries: true
  });

  const [deleteUniversityStaff, { loading: deletingStaff }] = useMutation(DELETE_UNIVERSITY_STAFF_MUTATION, {
    onCompleted: (result) => {
      if (result.deleteUniversityStaff.success) {
        showSnackbar(result.deleteUniversityStaff.message || "Staff member removed");
        setIsRemoveDialogOpen(false);
        setSelectedStaffMember(null);
      } else {
        showErrorSnackbar(result.deleteUniversityStaff.message || "Failed to remove staff member");
      }
    },
    onError: () => showErrorSnackbar("Failed to remove staff member"),
    refetchQueries: [refetchQuery],
    awaitRefetchQueries: true
  });

  const staffMembers = data?.universities[0]?.universityStaff || [];

  const handleAddStaffMember = async (userId: string, role: UniversityStaffRole) => {
    await createUniversityStaff({
      variables: {
        input: {
          universityId: universityLegacyId,
          userId,
          role
        }
      }
    });
  };

  const handleUpdateStaffRole = async (role: UniversityStaffRole) => {
    if (!selectedStaffMember) return;

    await updateUniversityStaff({
      variables: {
        input: {
          id: selectedStaffMember.id,
          role
        }
      }
    });
  };

  const handleRemoveStaffMember = async () => {
    if (!selectedStaffMember) return;

    await deleteUniversityStaff({
      variables: {
        id: selectedStaffMember.id
      }
    });
  };

  const columns = [
    ...buildAvatarDetailsColumn<UniversityStaffMember>({
      isCompactLayout,
      getTitle: (row) => row.user.name,
      getSubtitle: (row) => row.user.email || "",
      subtitleLabel: "Email",
      getPicture: (row) => row.user.photo || undefined,
      header: "Name",
      size: 260
    }),
    buildCopyTextColumn<UniversityStaffMember>({
      accessorKey: "user.email",
      header: "Email",
      copyFieldLabel: "Email",
      getText: (row) => row.user.email || "",
      shouldShowCopyButton: (row) => !!row.user.email,
      enableSorting: false
    }),
    {
      accessorFn: (row: UniversityStaffMember) => UNIVERSITY_STAFF_ROLE_LABELS[row.role],
      header: "Role",
      id: "role",
      enableSorting: false
    },
    {
      accessorFn: (row: UniversityStaffMember) => row.updatedAt,
      header: "Modified",
      id: "updatedAt",
      enableSorting: false,
      cell: ({ getValue }: { getValue: () => string }) => {
        const date = getValue();
        return renderEmptyCellState(date ? toFormat(date) : null);
      }
    },
    buildActionColumn<UniversityStaffMember & { id: string }>({
      getActions: (row) => [
        {
          text: "Change Role",
          onClick: () => {
            setSelectedStaffMember(row);
            setIsEditModalOpen(true);
          }
        },
        {
          text: "Remove from University",
          iconName: "delete",
          destructive: true,
          onClick: () => {
            setSelectedStaffMember(row);
            setIsRemoveDialogOpen(true);
          }
        }
      ],
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <TableHeading
        heading="University Staff"
        body={`Manage who receives ${universityName || "this university"} notification emails and whether they are assigned as a director or staff member.`}
        actions={[
          {
            label: "Add Staff Member",
            icon: "add",
            variant: "filled",
            onClick: () => setIsAddModalOpen(true)
          }
        ]}
      />
      <Table
        data={staffMembers}
        loading={loading}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Staff Members"
            caption="Add staff members to control who receives university notification emails."
            iconName="badge"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={staffMembers.length} />}
      />

      <AddStaffMemberModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddStaffMember}
        isLoading={creatingStaff}
      />

      <EditStaffRoleModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStaffMember(null);
        }}
        onSave={handleUpdateStaffRole}
        staffMember={selectedStaffMember}
        isLoading={updatingStaff}
      />

      <RemoveStaffMemberDialog
        open={isRemoveDialogOpen}
        onClose={() => {
          setIsRemoveDialogOpen(false);
          setSelectedStaffMember(null);
        }}
        onConfirm={handleRemoveStaffMember}
        isLoading={deletingStaff}
        staffName={selectedStaffMember?.user.name}
        roleTitle={selectedStaffMember ? UNIVERSITY_STAFF_ROLE_LABELS[selectedStaffMember.role] : undefined}
      />
    </>
  );
}
